import type { Session } from '@supabase/supabase-js';
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { setWorkspaceScope } from '../lib/active-tenant';
import { supabase } from '../lib/supabase';

interface AuthState {
  session: Session | null;
  loading: boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    let initialized = false;

    const applySession = (nextSession: Session | null, nextLoading = false) => {
      setWorkspaceScope(nextSession?.user.id);
      setSession(nextSession);
      setLoading(nextLoading);
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!initialized && event === 'INITIAL_SESSION') return;
      applySession(nextSession);
    });

    void supabase.auth.getSession().then(async ({ data }) => {
      let nextSession = data.session;

      if (nextSession) {
        const { data: userData, error } = await supabase.auth.getUser(nextSession.access_token);
        if (error || !userData.user) {
          await supabase.auth.signOut({ scope: 'local' });
          nextSession = null;
        }
      }

      initialized = true;
      if (active) applySession(nextSession);
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  const value = useMemo(() => ({ session, loading }), [session, loading]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const state = useContext(AuthContext);
  if (!state) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return state;
}
